import { PlusIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { validateMeterImportRow } from "@/lib/schemas/meter-import.schema";
import { ImportMetersEditorRow } from "./import-meters-editor-row";
import type { MeterImportRow, RowErrorsMap } from "./import-meters-types";
import { findDuplicateSerials, makeEmptyRow } from "./import-meters-types";

interface ImportMetersEditorProps {
	rows: MeterImportRow[];
	onRowsChange: (rows: MeterImportRow[]) => void;
	onSubmit: () => void;
	onBack: () => void;
	isPending: boolean;
}

export function ImportMetersEditor({
	rows,
	onRowsChange,
	onSubmit,
	onBack,
	isPending,
}: ImportMetersEditorProps) {
	const { t } = useTranslation();

	const duplicates = useMemo(() => findDuplicateSerials(rows), [rows]);
	const errorsMap = useMemo<RowErrorsMap>(() => {
		const map: RowErrorsMap = {};
		for (const r of rows) {
			map[r._id] = validateMeterImportRow(r);
		}
		return map;
	}, [rows]);

	const errorCount = useMemo(() => {
		let n = 0;
		for (const r of rows) {
			if (Object.keys(errorsMap[r._id] ?? {}).length > 0) n += 1;
			else if (duplicates.has(r.serial_number.trim()) && r.serial_number.trim())
				n += 1;
		}
		return n;
	}, [rows, errorsMap, duplicates]);

	const canSubmit = rows.length > 0 && errorCount === 0 && !isPending;

	function patchRow(id: string, patch: Partial<MeterImportRow>) {
		onRowsChange(rows.map((r) => (r._id === id ? { ...r, ...patch } : r)));
	}
	function deleteRow(id: string) {
		onRowsChange(rows.filter((r) => r._id !== id));
	}
	function addRow() {
		onRowsChange([...rows, makeEmptyRow()]);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4">
			<div className="flex items-center justify-between">
				<span className="text-sm text-muted-foreground">
					{t("meterImport.rowSummary", { count: rows.length })}
					{errorCount > 0 && (
						<span className="ml-2 text-destructive">
							{t("meterImport.errorSummary", { count: errorCount })}
						</span>
					)}
				</span>
				<Button variant="outline" size="sm" onClick={addRow}>
					<PlusIcon aria-hidden="true" />
					{t("meterImport.addRow")}
				</Button>
			</div>

			<div className="min-h-0 flex-1 overflow-auto rounded-md border">
				<table className="w-full text-sm">
					<thead className="sticky top-0 z-10 bg-muted/50">
						<tr className="border-b text-left">
							<th className="px-2 py-2 font-medium">
								{t("meterImport.col.serial_number")}
							</th>
							<th className="px-2 py-2 font-medium">
								{t("meterImport.col.encryption_key")}
							</th>
							<th className="w-28 px-2 py-2 font-medium">
								{t("meterImport.col.type")}
							</th>
							<th className="px-2 py-2 font-medium">
								{t("meterImport.col.street")}
							</th>
							<th className="px-2 py-2 font-medium">
								{t("meterImport.col.apartment_number")}
							</th>
							<th className="w-12" />
						</tr>
					</thead>
					<tbody>
						{rows.map((r) => (
							<ImportMetersEditorRow
								key={r._id}
								row={r}
								errors={errorsMap[r._id] ?? {}}
								isDuplicateSerial={
									!!r.serial_number.trim() &&
									duplicates.has(r.serial_number.trim())
								}
								onChange={(patch) => patchRow(r._id, patch)}
								onDelete={() => deleteRow(r._id)}
							/>
						))}
					</tbody>
				</table>
			</div>

			<div className="flex items-center justify-between gap-2">
				<Button variant="outline" onClick={onBack} disabled={isPending}>
					{t("meterImport.back")}
				</Button>
				<Button onClick={onSubmit} disabled={!canSubmit}>
					{isPending ? t("meterImport.uploading") : t("meterImport.upload")}
				</Button>
			</div>
		</div>
	);
}
